/**
 * GameResults Constants
 * Centralized constants for gameResults feature to avoid typos and enable reuse
 */

/**
 * Filter key constants (used in FilterButtons component)
 * Used to identify filter types in UI
 */
export const FILTER_KEYS = {
  DATE: 'date',
  LEAGUE: 'league',
} as const;

/**
 * Filter type constants
 * Used for sorting and filtering game results
 */
export const FILTER_TYPES = {
  DATE: 'date',
  LEAGUE: 'league',
  DATE_DESC: 'date-desc',
  DATE_ASC: 'date-asc',
} as const;

/**
 * Date sort type constants
 * Used for sorting games by date
 */
export const DATE_SORT_TYPES = {
  NEWEST: 'newest',
  OLDEST: 'oldest',
} as const;

/**
 * Game status constants
 * Used to categorize games by their current state
 */
export const GAME_STATUSES = {
  LIVE: 'live',
  COMPLETED: 'completed',
  UPCOMING: 'upcoming',
} as const;

/** Label shown above score for completed matches in scoreboard row */
export const SCOREBOARD_COMPLETED_LABEL = 'Final';

/**
 * Game section constants
 * Used for grouping and displaying games in sections
 */
export const GAME_SECTIONS = {
  LIVE_NOW: {
    TITLE: 'Live Now',
    ICON: '🔴',
    STATUS: GAME_STATUSES.LIVE,
  },
  RECENT_RESULTS: {
    TITLE: 'Recent Results',
    ICON: '✅',
    STATUS: GAME_STATUSES.COMPLETED,
  },
  UPCOMING: {
    TITLE: 'Upcoming',
    ICON: '📅',
    STATUS: GAME_STATUSES.UPCOMING,
  },
} as const;

/**
 * Section filter keys for game results (Upcoming / Recent / Live)
 * Used by section filter chips to show one section at a time
 */
export const SECTION_FILTER_KEYS = {
  ALL: 'all',
  LIVE: 'live',
  RECENT: 'recent',
  UPCOMING: 'upcoming',
} as const;

export type SectionFilterKey = (typeof SECTION_FILTER_KEYS)[keyof typeof SECTION_FILTER_KEYS];

/**
 * Section filter options for chip labels and keys
 */
export const SECTION_FILTER_OPTIONS: ReadonlyArray<{
  key: SectionFilterKey;
  label: string;
}> = [
  { key: SECTION_FILTER_KEYS.ALL, label: 'All' },
  { key: SECTION_FILTER_KEYS.LIVE, label: GAME_SECTIONS.LIVE_NOW.TITLE },
  { key: SECTION_FILTER_KEYS.RECENT, label: GAME_SECTIONS.RECENT_RESULTS.TITLE },
  { key: SECTION_FILTER_KEYS.UPCOMING, label: GAME_SECTIONS.UPCOMING.TITLE },
] as const;

/**
 * Empty state messages when no games match the current section filter
 */
export const EMPTY_STATE_MESSAGES: Record<SectionFilterKey, string> = {
  [SECTION_FILTER_KEYS.ALL]: 'No matches found',
  [SECTION_FILTER_KEYS.LIVE]: 'No live games right now',
  [SECTION_FILTER_KEYS.RECENT]: 'No recent results',
  [SECTION_FILTER_KEYS.UPCOMING]: 'No upcoming games',
} as const;

/**
 * Short subtitles for empty states (guidance or personality)
 */
export const EMPTY_STATE_SUBTITLES: Record<SectionFilterKey, string> = {
  [SECTION_FILTER_KEYS.ALL]: 'Try another league or date to see games.',
  [SECTION_FILTER_KEYS.LIVE]: 'Check back soon — something’s always heating up.',
  [SECTION_FILTER_KEYS.RECENT]: 'Finished games will show up here.',
  [SECTION_FILTER_KEYS.UPCOMING]: 'Upcoming matches will appear here.',
} as const;

/** Empty state when the selected calendar date has no games (scoreboard tab). */
export const SCOREBOARD_EMPTY_DATE = {
  TITLE: 'No games on this day',
  SUBTITLE: 'Pick another date from the calendar above.',
  ICON: 'event',
} as const;

/** Empty state when view by league and selected league has no games. */
export const SCOREBOARD_EMPTY_LEAGUE = {
  TITLE: 'No matches for this league',
  SUBTITLE: 'Try another league or date.',
  ICON: 'sports-soccer',
} as const;

/** View mode values (scoreboard tab: date vs league). */
export const SCOREBOARD_VIEW_MODE_KEYS = {
  DATE: 'date',
  LEAGUE: 'league',
} as const;

export type ScoreboardViewModeKey =
  (typeof SCOREBOARD_VIEW_MODE_KEYS)[keyof typeof SCOREBOARD_VIEW_MODE_KEYS];

/** Short labels for compact segment control */
export const SCOREBOARD_VIEW_MODE_SEGMENT = {
  DATE: 'Date',
  LEAGUE: 'League',
} as const;

/** View options for scoreboard: "View by" label + pill that opens sheet (Newest / Oldest / League) */
export const VIEW_OPTION_KEYS = {
  DATE_NEWEST: 'date-newest',
  DATE_OLDEST: 'date-oldest',
  LEAGUE: 'league',
} as const;

export type ViewOptionKey = (typeof VIEW_OPTION_KEYS)[keyof typeof VIEW_OPTION_KEYS];

/** Default league when switching to league view (case-insensitive match against name). */
export const DEFAULT_LEAGUE_NAME = 'Premier League';

/** date-fns format string for date headers in league-by-date view. */
export const LEAGUE_DATE_HEADER_FORMAT = 'EEEE, d MMM';

/** Segment options for the Date | League view mode switcher on the scoreboard. */
export const SCOREBOARD_VIEW_MODE_OPTIONS = [
  { key: SCOREBOARD_VIEW_MODE_KEYS.DATE, label: SCOREBOARD_VIEW_MODE_SEGMENT.DATE },
  { key: SCOREBOARD_VIEW_MODE_KEYS.LEAGUE, label: SCOREBOARD_VIEW_MODE_SEGMENT.LEAGUE },
] as const;

